import rawSettings from '@/shared/config/panel.settings.json';
import type { GroupTone } from '@/shared/config/approval.mapping';
import type { PanelSettings } from '@/shared/config/panel.settings';

/**
 * O que a parametrização da API **não** traz.
 *
 * Título, colunas do grid, abas (status) e trilhas (perfis) vêm de
 * `GET /approval/config` em runtime — ver `approval.mapping.ts` e
 * `EventConfigProvider`. Aqui ficou só o resto, que não existe no documento gravado
 * no banco:
 *
 * - **transições permitidas** entre status e a **ação em lote**: são regras de
 *   fluxo, e o wizard não as coleta;
 * - **rótulos em 3ª pessoa** ("aprovada", "não aprovada") das mensagens de sucesso;
 * - **tamanho de página**;
 * - o **evento no MICE**, que é a chave para buscar a parametrização, e o título de
 *   reserva para documento gravado sem título;
 * - **classes de estilo**: o tom é semântico e a tradução para tokens do design
 *   system é decisão de interface — classe do Tailwind em JSON de parametrização
 *   quebraria silenciosamente numa troca de tema.
 *
 * Cada um destes é candidato a migrar para o documento quando o builder passar a
 * coletá-lo.
 */

const settings = rawSettings as PanelSettings;

/** Chave de status. Vem da API, então é texto — não uma união fechada. */
export type GroupKey = string;

/**
 * Nome do evento para títulos e rodapés — **reserva**, usada até o título vir da API.
 *
 * O id do evento não está aqui: é `env.miceEventId` (`VITE_MICE_EVENT_ID`). Ele
 * identifica o deploy, não o painel, e manter os dois no mesmo objeto convidava a
 * tratar um valor de ambiente como ajuste versionado.
 */
export const EVENT = {
  title: settings.event.title,
  edition: settings.event.edition ?? '',
  /** Nome e edição na mesma linha, ex.: "Convenção Anual 2026". */
  fullTitle: [settings.event.title, settings.event.edition].filter(Boolean).join(' '),
} as const;

/** Aba aberta ao entrar no painel, quando a API a oferece. */
export const DEFAULT_GROUP_KEY: string = settings.workflow.defaultGroup;

/**
 * Para onde um status pode ir.
 *
 * Sem declaração para a chave, todos os outros status valem: é o que mantém o
 * painel operável se a API trouxer um status que estes ajustes não conhecem —
 * melhor uma ação a mais do que uma linha sem ação nenhuma.
 */
export function transitionsFor(key: string, availableKeys: readonly string[]): string[] {
  const declared = settings.workflow.transitions[key];
  const allowed = declared ? declared.filter(target => availableKeys.includes(target)) : availableKeys.filter(target => target !== key);
  return [...allowed];
}

/** Rótulo em 3ª pessoa para as mensagens ("Inscrição de X aprovada."). */
export function pastLabelFor(key: string, fallback: string): string {
  return settings.pastLabels[key] ?? fallback.toLowerCase();
}

/** Ação em lote. `scope: 'page'` porque a API não expõe operação em lote. */
export const BULK_APPROVE = settings.workflow.bulkApprove;

/** Registros por página; `maxPageSize` é o teto que a API também aplica. */
export const LISTING = {
  pageSize: settings.listing.pageSize,
  maxPageSize: settings.listing.maxPageSize,
} as const;

/** Tom semântico → tokens do design system. Único ponto de tradução. */
const TONE_STYLES: Record<GroupTone, { dotClassName: string; textClassName: string; softClassName: string }> = {
  approved: { dotClassName: 'bg-approved', textClassName: 'text-approved', softClassName: 'bg-approved-soft text-approved' },
  rejected: { dotClassName: 'bg-rejected', textClassName: 'text-rejected', softClassName: 'bg-rejected-soft text-rejected' },
  pending: { dotClassName: 'bg-pending', textClassName: 'text-pending', softClassName: 'bg-pending-soft text-pending' },
};

export function styleForTone(tone: GroupTone) {
  return TONE_STYLES[tone];
}
